/**
 * Capture mode (E1.5.S1) — the client-LOCAL presentation slice for the TECHDEMO
 * screenshot/video pipeline. Purely cosmetic: it hides HUD/debug chrome and
 * parameterises the orbit camera into named presets so the owner can record
 * clean footage of the locked `techdemo` seed. It sends NOTHING on the wire and
 * grants NO power (AD-021/AD-124 untouched); camera presets stay strictly within
 * CAMERA_BOUNDS (camera.ts) — the zoom-out envelope is never widened.
 *
 * The logic worth testing (preset table, capture-state derivation, slow-orbit
 * maths, preset cycling, pref codec) lives in these pure functions so the AD-120
 * Node harness can exercise them without a DOM; the React/Three wiring stays thin
 * (CaptureController.tsx + Game.ts + camera.ts).
 */

import { CAMERA_BOUNDS } from './camera.js';

/** localStorage keys — the `pxl.*` presentation-preference namespace (environments.md §E1.5). */
export const CAPTURE_KEYS = {
  /** Master capture toggle: footage-clean DOM (hotkey F4). */
  captureMode: 'pxl.captureMode',
  /** Standalone HUD on/off, independent of capture mode (hotkey F3). */
  hudHidden: 'pxl.hudHidden',
  /** In-scene health-bar sprite visibility sub-flag (default ON within capture mode). */
  captureHideHealthBars: 'pxl.captureHideHealthBars',
  /**
   * Suppress the self-view equipment meshes (held weapons + worn armour) for the
   * duration of a deliberately gear-free body/animation diagnostic. Default OFF
   * within capture mode: the player-rig-v2 equipment re-seat has landed, so the
   * seeded loadout is part of the character presentation that footage must show.
   * Set `pxl.captureHideEquipment='1'` to opt out explicitly.
   */
  captureHideEquipment: 'pxl.captureHideEquipment',
  /**
   * Hide the WR.2 landmark markers (shrine cone / transition torus / poi post)
   * from footage. Default ON within capture mode: they are wayfinding aids, not
   * scenery, and the cyan shrine cone dominated every shot of the 2026-W37
   * devlog video (owner report, 2026-09-09). Set `pxl.captureHideMarkers='0'`
   * to film them deliberately. Normal play is never affected.
   */
  captureHideMarkers: 'pxl.captureHideMarkers',
  /** Last-applied camera preset id. */
  capturePreset: 'pxl.capturePreset',
  /** Slow-orbit drift toggle. */
  slowOrbit: 'pxl.captureSlowOrbit',
  /**
   * Load the full-resolution TEXTURED renders (each entry's base `url`) for
   * showcase placements instead of the lightweight untextured `lods[0]` proxies.
   * Defaults ON inside capture mode so devlog footage shows the real painted art
   * (nimbus is re-textured); set to '0' to A/B against the untextured proxies.
   * Presentation-only, capture-side (never affects normal gameplay LOD).
   */
  capturedTextures: 'pxl.captureTextured',
} as const;

/** A named camera pose. Angles/distance are constrained to CAMERA_BOUNDS by cameraPresetsValid(). */
export interface CameraPreset {
  readonly id: string;
  readonly label: string;
  readonly yaw: number;
  readonly pitch: number;
  readonly dist: number;
}

// Poses chosen WITHIN camera.ts bounds (MIN_DIST 3 / MAX_DIST 18 / pitch 0.15–1.45).
// `follow-default` matches the OrbitCamera field defaults (yaw π, pitch 0.8, dist 9)
// so applying it restores the ordinary play framing.
// A non-empty tuple so index 0 is statically known-present under
// noUncheckedIndexedAccess (FALLBACK_PRESET is the typed head).
export const CAMERA_PRESETS = [
  { id: 'follow-default', label: 'Follow (default)', yaw: Math.PI, pitch: 0.8, dist: 9 },
  { id: 'close', label: 'Close', yaw: Math.PI, pitch: 0.55, dist: 4.5 },
  { id: 'three-quarter', label: 'Three-quarter', yaw: Math.PI * 0.75, pitch: 0.7, dist: 8 },
  { id: 'wide', label: 'Wide', yaw: Math.PI, pitch: 1.05, dist: 17 },
] as const satisfies readonly [CameraPreset, ...CameraPreset[]];

/** The pose applied when nothing is stored / a stored id is unknown. */
const FALLBACK_PRESET: CameraPreset = CAMERA_PRESETS[0];

/** The id applied when nothing is stored / a stored id is unknown. */
export const DEFAULT_PRESET_ID = FALLBACK_PRESET.id;

/** Slow-orbit yaw rate (radians per second). A full revolution ≈ 60 s — gentle for beauty shots. */
export const SLOW_ORBIT_RAD_PER_SEC = (2 * Math.PI) / 60;

/**
 * Validate that every preset pose sits inside CAMERA_BOUNDS. Wired into a
 * table-driven test so a future preset edit that strays outside the envelope
 * fails CI rather than silently widening the effective zoom-out range.
 */
export function cameraPresetsValid(presets: readonly CameraPreset[] = CAMERA_PRESETS): boolean {
  return presets.every(
    (p) =>
      p.dist >= CAMERA_BOUNDS.MIN_DIST &&
      p.dist <= CAMERA_BOUNDS.MAX_DIST &&
      p.pitch >= CAMERA_BOUNDS.MIN_PITCH &&
      p.pitch <= CAMERA_BOUNDS.MAX_PITCH &&
      Number.isFinite(p.yaw),
  );
}

/** Resolve a preset id to its pose, falling back to the default for an unknown id. */
export function presetById(id: string | null | undefined): CameraPreset {
  return CAMERA_PRESETS.find((p) => p.id === id) ?? FALLBACK_PRESET;
}

/**
 * The next preset id when cycling with `[` / `]`. `dir` is -1 (previous) or +1
 * (next); wraps around the table. An unknown current id starts the cycle from
 * the first preset.
 */
export function cyclePresetId(current: string | null | undefined, dir: 1 | -1): string {
  const n = CAMERA_PRESETS.length;
  const idx = CAMERA_PRESETS.findIndex((p) => p.id === current);
  const base = idx < 0 ? 0 : idx;
  const next = (((base + dir) % n) + n) % n;
  return (CAMERA_PRESETS[next] ?? FALLBACK_PRESET).id;
}

/** What a capture hotkey does. */
export type CaptureHotkeyAction =
  | 'toggleCapture'
  | 'toggleHud'
  | 'presetPrev'
  | 'presetNext'
  | 'toggleSlowOrbit';

/**
 * The capture hotkey table, keyed on `KeyboardEvent.key`. `\` toggles slow-orbit
 * MID-SESSION (q-capture-camera-channel-devcam-bridge, AD-605 §4): the studio's
 * in-shot schedule can only hold or press a key, so a bounded pan is slow-orbit
 * ON for N seconds and OFF again — a key tap, not a camera API. The drift itself
 * stays inside CAMERA_BOUNDS (yaw only; pitch and distance are untouched).
 * `\` collides with no Shell panel hotkey (letters, F1/F2/F9, backtick).
 */
export const CAPTURE_HOTKEYS: Readonly<Record<string, CaptureHotkeyAction>> = {
  F4: 'toggleCapture',
  F3: 'toggleHud',
  '[': 'presetPrev',
  ']': 'presetNext',
  '\\': 'toggleSlowOrbit',
};

/** The action bound to a `KeyboardEvent.key`, or null when the key is not a capture hotkey. */
export function captureHotkeyAction(key: string): CaptureHotkeyAction | null {
  return Object.prototype.hasOwnProperty.call(CAPTURE_HOTKEYS, key)
    ? (CAPTURE_HOTKEYS[key] ?? null)
    : null;
}

/** The flag inputs that determine what capture mode hides. */
export interface CaptureFlags {
  captureMode: boolean;
  hudHidden: boolean;
  hideHealthBars: boolean;
}

/**
 * What the DOM/scene should render given the flags. Pure — the single source of
 * truth for "what is hidden", exercised directly by tests and consumed by the
 * React wiring (CaptureController) and the render loop (Game.ts health bars).
 *
 * - Capture mode is the master footage-clean state: it hides the HUD, the
 *   launcher bar, the connection ministats, and suppresses open Shell windows
 *   (incl. Debug). Its DOM must leave nothing in frame.
 * - The standalone HUD toggle hides only `pxl-hud` (launcher + ministats stay).
 * - Health bars are in-scene sprites: hidden only within capture mode, and only
 *   when the sub-flag is on (default), so fight-stage clips can turn them back on.
 */
export interface CaptureView {
  hudVisible: boolean;
  launcherVisible: boolean;
  ministatsVisible: boolean;
  windowsSuppressed: boolean;
  healthBarsVisible: boolean;
}

export function captureView(flags: CaptureFlags): CaptureView {
  const cap = flags.captureMode;
  return {
    hudVisible: !cap && !flags.hudHidden,
    launcherVisible: !cap,
    ministatsVisible: !cap,
    windowsSuppressed: cap,
    // Sprites are gated by the sub-flag ONLY inside capture mode; outside it they
    // follow their normal in-scene visibility (Game.ts decides ratio-based show).
    healthBarsVisible: !(cap && flags.hideHealthBars),
  };
}

/** Slow-orbit yaw advance for a frame of `dtMs` milliseconds (presentation-only drift). */
export function slowOrbitYawDelta(dtMs: number): number {
  if (!Number.isFinite(dtMs) || dtMs <= 0) return 0;
  return (Math.min(dtMs, 100) / 1000) * SLOW_ORBIT_RAD_PER_SEC;
}

// ---- thin localStorage codec (0/1 flags + string preset) ----
// Browser-only side; the pure logic above never touches storage. Guarded so a
// non-DOM context (tests, SSR) is a no-op read rather than a throw.

function ls(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readFlag(key: string, fallback = false): boolean {
  const raw = ls()?.getItem(key);
  if (raw === null || raw === undefined) return fallback;
  return raw === '1';
}

export function writeFlag(key: string, value: boolean): void {
  ls()?.setItem(key, value ? '1' : '0');
}

export function readPresetId(): string {
  const raw = ls()?.getItem(CAPTURE_KEYS.capturePreset);
  return presetById(raw).id;
}

export function writePresetId(id: string): void {
  ls()?.setItem(CAPTURE_KEYS.capturePreset, presetById(id).id);
}

/** Read the current capture flag set (health-bar hiding defaults ON — beauty shots). */
export function readCaptureFlags(): CaptureFlags {
  return {
    captureMode: readFlag(CAPTURE_KEYS.captureMode),
    hudHidden: readFlag(CAPTURE_KEYS.hudHidden),
    hideHealthBars: readFlag(CAPTURE_KEYS.captureHideHealthBars, true),
  };
}

/**
 * Whether showcase placements should load the full TEXTURED renders rather than
 * the untextured proxy LODs. ON only within capture mode, and there by default
 * (set `pxl.captureTextured='0'` to compare against the proxies). Browser-side
 * read; a non-DOM context is a safe `false`.
 */
export function capturedTexturesOn(): boolean {
  return readFlag(CAPTURE_KEYS.captureMode) && readFlag(CAPTURE_KEYS.capturedTextures, true);
}

/**
 * Capture-only override for the session asset-detail policy. Outside capture
 * mode there is no override; inside it, full detail defaults on and an explicit
 * `pxl.captureTextured='0'` requests the proxy comparison.
 */
export function capturedTexturesPreference(): boolean | null {
  if (!readFlag(CAPTURE_KEYS.captureMode)) return null;
  return readFlag(CAPTURE_KEYS.capturedTextures, true);
}

/**
 * Whether the self-view equipment meshes should be suppressed right now.
 *
 * ON only within capture mode, and there by default — see
 * `CAPTURE_KEYS.captureHideEquipment` for why. Normal play is never affected:
 * outside capture mode this is always `false`, whatever the sub-flag says.
 * Browser-side read; a non-DOM context is a safe `false`.
 */
export function captureEquipmentHidden(): boolean {
  return readFlag(CAPTURE_KEYS.captureMode) && readFlag(CAPTURE_KEYS.captureHideEquipment, false);
}

/**
 * Whether the landmark markers should be hidden from the scene right now.
 *
 * ON only within capture mode, and there BY DEFAULT — see
 * `CAPTURE_KEYS.captureHideMarkers`. Outside capture mode this is always `false`
 * (markers are gameplay wayfinding), whatever the sub-flag says. Browser-side
 * read; a non-DOM context is a safe `false`.
 */
export function captureMarkersHidden(): boolean {
  return readFlag(CAPTURE_KEYS.captureMode) && readFlag(CAPTURE_KEYS.captureHideMarkers, true);
}
